import { useMemo, useEffect, useState, useCallback } from "react";
import {
  MantineReactTable,
  useMantineReactTable,
  MRT_ToggleFiltersButton,
  MRT_ShowHideColumnsButton,
  MRT_ToggleDensePaddingButton,
  MRT_ToggleFullScreenButton,
} from "mantine-react-table";
import { MRT_Localization_EN } from "mantine-react-table/locales/en";
import { MRT_Localization_FR } from "mantine-react-table/locales/fr";
import { useNavigate } from "react-router-dom";
import { Box, useMantineColorScheme, Button, Menu, TextInput, Table, Grid, Col, Stack, Avatar, Badge, Text, Group, ActionIcon, Tooltip } from "@mantine/core";
import { useAtom } from "jotai";
import { SeIcon } from "@se/web-ui-react";
import { useTranslation } from "react-i18next";
import "../../pages/Dashboard/style.scss";
import "./dataTable.scss";
import { statusDTO } from "../../constants/statusDTO";
import mgaConstants from "../../constants/index";
import { componentsLoaderAtom } from "../../utils/atoms/componentsLoader";
import { collapseAtom } from "../../utils/atoms/collapseAtom";
import { activeTabAtom } from "../../utils/atoms/activeTabAtom";
import { useDynamicTableHeight } from "../../hooks/useDynamicTableHeight";
import { userScopeAtom } from "../../utils/atoms/userScopeAtom";
import { IconShare, IconTrash, IconCopy, IconRefresh, IconWorld, IconMap, IconTable, IconX } from "@tabler/icons-react";
import MGAAPI from "../../api";
import SEConfirmModal from "../common/SEConfirmModal";
import ProjectMapView from "./ProjectMapView";
import { convertPictureToBlobUrl } from "../../constants/helper";

const DataTable = (props) => {
  const [userScope] = useAtom(userScopeAtom);
  const [componentsLoader] = useAtom(componentsLoaderAtom);
  const [, setCollapseSideMenu] = useAtom(collapseAtom);

  const { colorScheme } = useMantineColorScheme();
  const isLightMode = colorScheme === "light";
  const navigate = useNavigate();
  const [, setActiveState] = useAtom(activeTabAtom);
  const { t, i18n } = useTranslation();
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [shareEmail, setShareEmail] = useState("");
  const [shareProject, setShareProject] = useState(null);
  const [shareSnackbar, setShareSnackbar] = useState({ type: 'success', message: '' });
  const MGA_API = useMemo(() => new MGAAPI(), []);
  const highlightedProjectId = props.highlightProjectId;
  const [shareLoading, setShareLoading] = useState(false);
  const [copyModalOpen, setCopyModalOpen] = useState(false);
  const [copyName, setCopyName] = useState("");
  const [copyProjectTarget, setCopyProjectTarget] = useState(null);
  const [copyLoading, setCopyLoading] = useState(false);
  const [copySnackbar, setCopySnackbar] = useState({ type: 'success', message: '' });
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteProjectTarget, setDeleteProjectTarget] = useState(null);
  const [deleteSnackbar, setDeleteSnackbar] = useState({ type: 'success', message: '' });
  // Shared project info modal state
  const [sharedInfoModalOpen, setSharedInfoModalOpen] = useState(false);
  const [sharedUsers, setSharedUsers] = useState([]);
  const [sharedInfoLoading, setSharedInfoLoading] = useState(false);
  const [sharedUserProfileModalOpen, setSharedUserProfileModalOpen] = useState(false);
  const [sharedUserProfileLoading, setSharedUserProfileLoading] = useState(false);
  const [sharedUserProfileError, setSharedUserProfileError] = useState("");
  const [sharedUserProfileData, setSharedUserProfileData] = useState(null);
  const [showMap, setShowMap] = useState(false);
  const [mapResetCounter, setMapResetCounter] = useState(0);
  const localization = useMemo(() => {
    const lang = (i18n.language || "").toLowerCase();
    const base = lang.startsWith("fr") ? MRT_Localization_FR : MRT_Localization_EN;
    return {
      ...base,
      showHideColumns: t("dataTable.showHideColumns"),
      showHideFilters: t("dataTable.showHideFilters"),
      clearFilters: t("dataTable.clearFilters"),
      clearSearch: t("dataTable.clearSearch"),
      toggleDensity: t("dataTable.toggleDensity"),
      fullScreen: t("dataTable.fullScreen"),
      search: t("dataTable.search"),
      sortByColumnAsc: t("dataTable.sortAsc"),
      sortByColumnDesc: t("dataTable.sortDesc"),
      unsorted: t("dataTable.unsorted"),
      noRecordsToDisplay: t("dataTable.noRecords"),
    };
  }, [i18n.language, t]);

  // Permission flags
  const canShare = !!userScope?.access?.shareProject;
  const canDelete = !!userScope?.access?.deleteProject;
  const canCopy = !!userScope?.access?.copyProject;

  // Linear-time email validation to avoid regex backtracking issues
  const emailValid = useMemo(() => {
    const s = (shareEmail || '').trim();
    if (!s || s.length > 254) return false; // RFC max length safeguard
    if (s.includes(' ')) return false; // no spaces allowed
    const at = s.indexOf('@');
    if (at <= 0 || at !== s.lastIndexOf('@')) return false; // exactly one '@' and not first/last
    const local = s.slice(0, at);
    const domain = s.slice(at + 1);
    if (!local || !domain) return false;
    const dot = domain.indexOf('.');
    if (dot <= 0 || dot === domain.length - 1) return false; // dot exists, not first/last of domain
    // ensure each label in domain is non-empty
    if (domain.split('.').some(lbl => lbl.length === 0)) return false;
    return true;
  }, [shareEmail]);

  // Stable callbacks to prevent column re-renders causing perceived UI lag
  const handleShareClick = useCallback((rowOriginal) => {
    setShareProject(rowOriginal);
    setShareModalOpen(true);
  }, []);

  const handleCopyClick = useCallback((rowOriginal) => {
    setCopyProjectTarget(rowOriginal);
    setCopyName(`Copy - ${rowOriginal?.projectName}`);
    setCopyModalOpen(true);
  }, []);

  const handleDeleteClick = useCallback((rowOriginal) => {
    setDeleteProjectTarget(rowOriginal);
    setDeleteModalOpen(true);
  }, []);

  const handleSharedInfoClick = useCallback((rowOriginal) => {
    if (!rowOriginal?.projectId || sharedInfoLoading) return;
    setSharedInfoLoading(true);
    MGA_API.getSharedProject(rowOriginal.projectId)
      .then((res) => {
        // Expecting an array of email strings (possibly percent-encoded)
        const decoded = Array.isArray(res)
          ? res.map((e) => {
              try { return decodeURIComponent(e); } catch { return e; }
            })
          : [];
        setSharedUsers(decoded);
        setSharedInfoModalOpen(true);
      })
      .catch(() => {
        setSharedUsers([]);
        setSharedInfoModalOpen(true); // still open modal to show error/empty state
      })
      .finally(() => {
        setSharedInfoLoading(false);
      });
  }, [sharedInfoLoading, MGA_API]);

  const handleSharedUserClick = useCallback((email) => {
    if (!email || sharedUserProfileLoading) return;
    setSharedUserProfileModalOpen(true);
    setSharedUserProfileLoading(true);
    setSharedUserProfileError("");
    MGA_API.getUserProfileInfo(email)
      .then((res) => {
        setSharedUserProfileData(res || {});
      })
      .catch((e) => {
        setSharedUserProfileError(e?.message || 'Failed to load user');
        setSharedUserProfileData(null);
      })
      .finally(() => {
        setSharedUserProfileLoading(false);
      });
  }, [sharedUserProfileLoading, MGA_API]);

  const handleDeleteSubmit = useCallback(() => {
    if (!deleteProjectTarget?.projectId || deleteLoading) return;
    setDeleteLoading(true);
    MGA_API.deleteProject(deleteProjectTarget?.projectId)
      .then(() => {
        setDeleteSnackbar({ type: 'success', message: 'Project deleted successfully' });
        try { document.getElementById('delete-snackbar').open = true; } catch(e) {}
        setDeleteModalOpen(false);
        setDeleteProjectTarget(null);
        // Refresh list via parent callback if provided
        if (typeof props.refreshProjects === 'function') {
          props.refreshProjects();
        }
      })
      .catch(() => {
        setDeleteSnackbar({ type: 'error', message: 'Failed to delete project' });
        try { document.getElementById('delete-snackbar').open = true; } catch(e) {}
        // Keep modal open for retry
      })
      .finally(() => {
        setDeleteLoading(false);
      });
  }, [deleteProjectTarget, deleteLoading, MGA_API, props]);

  const handleCopySubmit = useCallback(() => {
    if (!copyProjectTarget?.projectId || !copyName?.trim() || copyLoading) return;
    setCopyLoading(true);
    MGA_API.copyProject(copyProjectTarget?.projectId, copyName?.trim())
      .then(() => {
        setCopySnackbar({ type: 'success', message: 'Project copied successfully' });
        try { document.getElementById('copy-snackbar').open = true; } catch(e) {}
        // Close only on success
        setCopyModalOpen(false);
        setCopyProjectTarget(null);
        
         if (typeof props.refreshProjects === 'function') {
          props.refreshProjects();
        }
      })
      .catch(() => {
        setCopySnackbar({ type: 'error', message: 'Failed to copy project' });
        try { document.getElementById('copy-snackbar').open = true; } catch(e) {}
        // Keep modal open for retry
      })
      .finally(() => {
        setCopyLoading(false);
        // setCopyName("");
      });
  }, [copyProjectTarget, copyName, copyLoading, MGA_API, props]);

  const handleShareSubmit = useCallback(() => {
    if (!shareProject?.projectId || !emailValid || shareLoading) return;
    setShareLoading(true);
  MGA_API.shareProject(shareProject?.projectId, shareEmail.trim())
      .then(() => {
        setShareSnackbar({ type: 'success', message: 'Project shared successfully' });
        try { document.getElementById('share-snackbar').open = true; } catch(e) {}
        // Close only on success
        setShareModalOpen(false);
        setShareEmail("");
        setShareProject(null);
      })
      .catch(() => {
        setShareSnackbar({ type: 'error', message: 'Failed to share project' });
        try { document.getElementById('share-snackbar').open = true; } catch(e) {}
        // Keep modal open for retry
      })
      .finally(() => {
        setShareLoading(false);
      });
  }, [shareProject, emailValid, shareLoading, MGA_API, shareEmail]);

  const projectRedirectHandler = useCallback((projectId, designId, designVersionId) => {
    setCollapseSideMenu((prev) => ({
      ...prev,
      leftMenu: true,
    }));
    setActiveState("DESIGN");
    navigate(
      `${mgaConstants.routes.mgaReports}/${projectId}/${designId}/${designVersionId}?tab=design`
    );
  }, [navigate, setActiveState, setCollapseSideMenu]);

  useEffect(() => {
    const theadElements = document.querySelectorAll("thead");
    theadElements.forEach((thead, index) => {
      thead.setAttribute(
        "data-intro",
        `Table header contains sort/filter/search features`
      );
      thead.setAttribute("data-position", "bottom");
    });
  }, []);
  const columns = useMemo(() => {
    const cols = [
      {
        accessorKey: "projectName",
        header: t("projectName"),
        size: 250,
        Cell: ({ cell }) => {
          const isHighlighted =
            highlightedProjectId &&
            String(highlightedProjectId) ===
              String(cell?.row?.original?.projectId);

          return (
            <span
              onClick={() => {
                projectRedirectHandler(
                  cell?.row?.original?.projectId,
                  cell?.row?.original?.designId,
                  cell?.row?.original?.designVersionId
                );
              }}
              style={{
                color: "#0087CD",
                cursor: "pointer",
                fontWeight: isHighlighted ? 700 : 400,
                display: "block",
                whiteSpace: "normal",
                overflowWrap: "anywhere",
                wordBreak: "break-word",
              }}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  projectRedirectHandler(
                    cell?.row?.original?.projectId,
                    cell?.row?.original?.designId,
                    cell?.row?.original?.designVersionId
                  );
                }
              }}
              data-intro={cell?.row?.index === 0 ? t("dataTable.projectNameIntro") : undefined}
              data-position={cell?.row?.index === 0 ? "bottom" : undefined}
            >
              {cell?.row?.original?.projectName}
            </span>
          );
        },
      },
      { accessorKey: "customerName", header: t("customerName"), size: 80 },
      { accessorKey: "cityCountry", header: t("dataTable.cityCountry"), size: 50, Cell: ({ cell }) => <>{cell.getValue()}</> },
      { accessorKey: "createdOn", header: t("dataTable.createdOn"), size: 50, sortingFn: "datetime", Cell: ({ cell }) => <>{cell?.getValue()?.split("T")?.[0]}</> },
      { accessorKey: "lastModifiedOn", header: t("dataTable.lastModifiedOn"), size: 50, sortingFn: "datetime", Cell: ({ cell }) => <>{cell?.getValue()?.split("T")?.[0]}</> },
      {
        accessorKey: "projectStatus",
        header: t("dataTable.projectStatus"),
        size: 70,
        Cell: ({ cell }) => (
          <div className="d-flex align-items-center" style={{ color: "#24B43F" }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
              {statusDTO?.[cell.getValue()]}
              {cell?.row?.original?.isShared && (
                <SeIcon
                  size="small"
                  style={{ display: 'inline-flex', cursor: 'pointer' }}
                  onClick={() => handleSharedInfoClick(cell?.row?.original)}
                  title={t("dataTable.viewSharedDetails")}
                >
                  user_group
                </SeIcon>
              )}
            </span>
          </div>
        ),
      },
    ];

    if (canShare || canDelete || canCopy) {
      cols.push({
        accessorKey: "action",
        header: " ",
        size: 5,
        enableSorting: false,
        enableColumnActions: false,
        enableGlobalFilter: false,
        enableColumnOrdering: false,
        enableColumnFilter: false,
        enableOrdering: false,
        enableColumnResizing: false,
        enableHiding: false,
        Cell: ({ cell }) => (
          <Menu shadow="md" width={140} position="bottom-right" withinPortal >
            <Menu.Target>
              {/* Using custom web component as trigger; kept minimal styling for faster paint */}
              <se-icon aria-label="project-row-actions-menu" slot="trigger" option="button" style={{ cursor: "pointer" }} title={t("common.action")}>
                other_vertical
              </se-icon>
            </Menu.Target>
            <Menu.Dropdown>
              {canCopy && (
                <Menu.Item icon={<IconCopy size={12} />} onClick={() => handleCopyClick(cell?.row?.original)}>{t("common.copy")}</Menu.Item>
              )}
              {canShare && (
                <Menu.Item icon={<IconShare size={12} />} onClick={() => handleShareClick(cell?.row?.original)}>{t("common.share")}</Menu.Item>
              )}
              {canDelete && (
                <Menu.Item icon={<IconTrash size={12} />} color="red" onClick={() => handleDeleteClick(cell?.row?.original)}>
                  {t("common.delete")}
                </Menu.Item>
              )}
            </Menu.Dropdown>
          </Menu>
        ),
      });
    }
    return cols;
  }, [projectRedirectHandler, handleShareClick, handleCopyClick, handleDeleteClick, handleSharedInfoClick, canShare, canDelete, canCopy, i18n.language, highlightedProjectId]);

  const data = useMemo(
    () =>
      props?.data?.map((project) => ({
        projectNo: project.projectNumber,
        projectName: project.projectName,
        customerName: project.customerName,
        designId: project.designWithVersionsDTOList?.[0]?.designDTO?.designId,
        designVersionId:
          project.designWithVersionsDTOList?.[0]?.designVersionDTO?.[0]?.designVersionId,
        cityCountry: project.locationDTO
          ? `${project.locationDTO.city || ""}${
              project.locationDTO.city && project.locationDTO.country
                ? ", "
                : ""
            }${project.locationDTO.country || ""}`
          : "",
        createdOn: project.createdOn,
        lastModifiedOn: project.updatedOn,
        projectStatus: project?.projectStatus,
        projectId: project?.projectId,
        isShared: project?.isShared,
        action: "Edit",
      })),
    [props.data]
  );
  const [isFullScreen, setIsFullScreen] = useState(false);

  const { pageSize, getTableHeight } = useDynamicTableHeight({
    rowHeight: 40,
    headerHeight: 48,
    footerHeight: 48,
    reservedSpace: 170,
    minRows: 5,
  });

  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize,
  });

  useEffect(() => {
    setPagination((prev) => ({ ...prev, pageSize }));
  }, [pageSize]);

  const table = useMantineReactTable({
    state: {
      isLoading: componentsLoader,
      isFullScreen,
      pagination,
    },
    localization,
    onIsFullScreenChange: setIsFullScreen,
    onPaginationChange: setPagination,
    columns,
    data,
    enableFullScreenToggle: true,
    enableStickyHeader: true,
    positionGlobalFilter: "right",
    initialState: {
      density: "xs",
      showGlobalFilter: true,
    },
    mantineTableContainerProps: {
      sx: {
        height: getTableHeight(),
        backgroundColor: isLightMode
          ? "#FFFFFF"
          : "var(--se-background-alternative)",
      },
    },
    mantinePaperProps: {
      withBorder: false,
      shadow: false,
      sx: {
        backgroundColor: isLightMode
          ? "#FFFFFF"
          : "var(--se-background-alternative)",
      },
    },
    mantineTableHeadCellProps: {
      sx: {
        fontWeight: "normal",
        fontSize: "14px",
        fontFamily: "Nunito",
        backgroundColor: isLightMode
          ? "#FFFFFF"
          : "var(--se-background-alternative)",
      },
    },
    mantineTableBodyProps: {
      sx: {
        backgroundColor: isLightMode
          ? "#FFFFFF"
          : "var(--se-background-alternative)",
      },
    },
    mantineTableBodyCellProps: ({ row }) => {
      const highlightMatch =
        highlightedProjectId &&
        String(highlightedProjectId) === String(row?.original?.projectId);
      const highlightColor = isLightMode
        ? "#f2f2f2"
        : "rgba(255,255,255,0.08)";
      return {
        sx: {
          fontWeight: "normal",
          fontSize: "14px",
          fontFamily: "Nunito",
          backgroundColor: highlightMatch
            ? highlightColor
            : isLightMode
              ? "#FFFFFF"
              : "var(--se-background-alternative)",
        },
      };
    },
    mantineTableBodyRowProps: ({ row }) => {
      const highlightMatch =
        highlightedProjectId &&
        String(highlightedProjectId) === String(row?.original?.projectId);
      if (!highlightMatch) return {};
      return {
        className: "datatable-row-highlight",
        style: {
          backgroundColor: isLightMode
            ? "#f2f2f2"
            : "rgba(255,255,255,0.08)",
          transition: "background-color 150ms ease",
        },
      };
    },
    mantineTableProps: {
      sx: {
        backgroundColor: isLightMode
          ? "#FFFFFF"
          : "var(--se-background-alternative)",
      },
    },
    mantineSearchTextInputProps: {
      placeholder: t("dataTable.searchPlaceholder"),
      sx: { minWidth: "400px" },
      // Tour attributes
      "data-intro": t("dataTable.searchIntro"),
      "data-position": "bottom",
    },
    mantineTopToolbarProps: {
      sx: {
        backgroundColor: isLightMode
          ? "#FFFFFF"
          : "var(--se-background-alternative)",
      },
    },
    mantineBottomToolbarProps: {
      sx: {
        padding: "10px",
        backgroundColor: isLightMode
          ? "#FFFFFF"
          : "var(--se-background-alternative)",
      },
    },
    enableSortingRemoval: false,
    globalFilterModeOptions: ["fuzzy"],
    renderTopToolbarCustomActions: ({ table }) => (
      <Box
        style={{
          color: !isLightMode ? "black" : "#ffffff",
          display: "flex",
          gap: "12px",
          alignItems: "center",
        }}
        className="box-header .step-1"
        data-intro={t("dataTable.createProject")}
        data-position="bottom"
      >
        <Button
          aria-label="project-table-create-project"
          option="raised"
          color="seGreen.0"
          icon="new_project"
          leftIcon={<SeIcon aria-hidden="true">new_project</SeIcon>}
          onClick={props.createProjectHandler}
          style={{
            color: !isLightMode ? "#2e3032" : "#ffffff",
          }}
          disabled={!userScope?.access?.createOrUpdateProject}
        >
          {t("createProject")}
        </Button>

        {!isFullScreen && (
          <Tooltip
            label={
              showMap
                ? t("dataTable.viewTable")
                : t("dataTable.viewMap")
            }
            withinPortal
          >
            <ActionIcon
              aria-label={showMap ? "project-table-switch-to-table" : "project-table-switch-to-map"}
              variant="subtle"
              onClick={() => setShowMap((v) => !v)}
              // title={
              //   showMap
              //     ? t("dataTable.viewTable", { defaultValue: "View Table" })
              //     : t("dataTable.viewMap", { defaultValue: "View Map" })
              // }
              sx={{
                color: isLightMode ? "#2e3032" : "#ffffff",
              }}
            >
              <IconMap size={22} style={{color: isLightMode ? "#868e96" :"#ffffff"}}/>
            </ActionIcon>
          </Tooltip>
        )}
      </Box>
    ),
    renderToolbarInternalActions: ({ table }) => (
      <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
       

        <MRT_ToggleFiltersButton table={table} />
        <MRT_ShowHideColumnsButton table={table} />
        <MRT_ToggleDensePaddingButton table={table} />
        <MRT_ToggleFullScreenButton table={table} />
      </Box>
    ),
  });

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape" && isFullScreen) {
        setIsFullScreen(false);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isFullScreen]);

  return (
    <>
  {showMap ? (
    <Box
      sx={{
        backgroundColor: isLightMode ? "#FFFFFF" : "var(--se-background-alternative)",
        height: isFullScreen ? "100vh" : getTableHeight(),
        minHeight: "500px",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "8px 10px",
          gap: "8px",
          zIndex: 1,
          flex: "0 0 auto",
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Button
            aria-label="project-map-create-project"
            option="raised"
            color="seGreen.0"
            icon="new_project"
            leftIcon={<SeIcon aria-hidden="true">new_project</SeIcon>}
            onClick={props.createProjectHandler}
            style={{
              color: !isLightMode ? "#2e3032" : "#ffffff",
            }}
            disabled={!userScope?.access?.createOrUpdateProject}
          >
            {t("createProject")}
          </Button>



          {/* Place View Table adjacent to Reset so position matches table toolbar */}
            <Tooltip
            label={t("dataTable.viewTable")}
            withinPortal
          >
            <ActionIcon
              aria-label="project-map-switch-to-table"
              variant="subtle"
              onClick={() => setShowMap(false)}
              // title={t("dataTable.viewTable", { defaultValue: "View Table" })}
              sx={{ color: isLightMode ? "#2e3032" : "#ffffff" }}
            >
              <IconTable size={22} style={{color: isLightMode ? "#868e96" :"#ffffff"}}/>

            </ActionIcon>
          </Tooltip>
                    <Tooltip label={t("dataTable.resetMap")} withinPortal>
            <ActionIcon
              aria-label="project-map-reset"
              variant="subtle"
              onClick={() => setMapResetCounter((c) => c + 1)}
              title={t("dataTable.resetMap")}
              sx={{ color: isLightMode ? "#2e3032" : "#ffffff" }}
            >
              {/* <IconRefresh size={22} /> */}
            </ActionIcon>
          </Tooltip>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Tooltip label={t("dataTable.close")} withinPortal>
            <ActionIcon
              aria-label="project-map-close"
              variant="subtle"
              onClick={() => setShowMap(false)}
              sx={{ color: isLightMode ? "#2e3032" : "#ffffff" }}
            >
              <IconX size={22} style={{color: isLightMode ? "#868e96" :"#ffffff"}}/>
            </ActionIcon>
          </Tooltip>
        </Box>
      </Box>
      <Box sx={{ flex: "1 1 auto", minHeight: 0 }}>
        <ProjectMapView
          projects={props?.data || []}
          height="100%"
          onProjectOpen={projectRedirectHandler}
          resetCounter={mapResetCounter}
        />
      </Box>
    </Box>
  ) : (
    <MantineReactTable table={table} class="maintain-zoom" />
  )}
  <se-loading loading={shareLoading || copyLoading || deleteLoading || sharedInfoLoading}></se-loading>
  <se-snackbar id="share-snackbar" type={shareSnackbar.type} icon="information_stroke" message={shareSnackbar.message} duration="3000"></se-snackbar>
  <se-snackbar id="copy-snackbar" type={copySnackbar.type} icon="information_stroke" message={copySnackbar.message} duration="3000"></se-snackbar>
  <se-snackbar id="delete-snackbar" type={deleteSnackbar.type} icon="information_stroke" message={deleteSnackbar.message} duration="3000"></se-snackbar>
      <SEConfirmModal
        opened={shareModalOpen}
        onClose={() => { setShareModalOpen(false); setShareEmail(""); }}
        onConfirm={handleShareSubmit}
        confirmLabel="Share"
        cancelLabel="Cancel"
        header="Share Project"
        message={(
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            <div>
              Share project <strong>{shareProject?.projectName}</strong> to below user:
            </div>
            <TextInput
              label="User Email"
              placeholder="name@example.com"
              value={shareEmail}
              onChange={(e) => setShareEmail(e.currentTarget.value)}
              error={shareEmail && !emailValid ? 'Enter a valid email' : null}
              required
            />
          </div>
        )}
        confirmDisabled={!emailValid || shareLoading}
        confirmLoading={shareLoading}
        confirmLoadingLabel="Sharing..."
      />
      <SEConfirmModal
        opened={copyModalOpen}
        onClose={() => { setCopyModalOpen(false); setCopyName(''); }}
        onConfirm={handleCopySubmit}
        confirmLabel="Copy"
        cancelLabel="Cancel"
        header="Copy Project"
        message={(
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            <div>Do you want to copy project <strong>{copyProjectTarget?.projectName}</strong>?</div>
            <TextInput
              label="New Project Name"
              placeholder="Copy - Project Name"
              value={copyName}
              onChange={(e) => setCopyName(e.currentTarget.value)}
              error={copyName === '' ? 'Name is required' : null}
              required
            />
          </div>
        )}
        confirmDisabled={!copyName?.trim() || copyLoading}
        confirmLoading={copyLoading}
        confirmLoadingLabel="Copying..."
      />
      <SEConfirmModal
        opened={deleteModalOpen}
        onClose={() => { setDeleteModalOpen(false); setDeleteProjectTarget(null); }}
        onConfirm={handleDeleteSubmit}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        header="Delete Project"
        message={(
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            <div>Do you want to delete project <strong>{deleteProjectTarget?.projectName}</strong>? This action cannot be undone.</div>
          </div>
        )}
        confirmDisabled={deleteLoading}
        confirmLoading={deleteLoading}
        confirmLoadingLabel="Deleting..."
      />
      <SEConfirmModal
        opened={sharedInfoModalOpen}
        onClose={() => { setSharedInfoModalOpen(false); setSharedUsers([]); }}
        onConfirm={() => { setSharedUsers([]); setSharedInfoModalOpen(false); }}
        confirmLabel="Close"
        showCancel={false}
        header="Shared Users"
        message={
          <div style={{ maxHeight:'50vh', overflow:'auto', minWidth:'340px' }}>
            {sharedInfoLoading ? (
              <Group position="center" p="md"><se-spinner size="large" /></Group>
            ) : sharedUsers.length > 0 ? (
              <Table striped highlightOnHover withTableBorder withColumnBorders>
                <thead>
                  <tr>
                    <th style={{ width:'48px' }}>#</th>
                    <th>Email</th>
                  </tr>
                </thead>
                <tbody>
                  {sharedUsers.map((email, idx) => (
                    <tr key={email} style={{ cursor:'pointer' }} onClick={() => handleSharedUserClick(email)}>
                      <td>{idx + 1}</td>
                      <td style={{ wordBreak:'break-all' }}>{email}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            ) : (
              <Text size="sm" color="dimmed">No users have been shared on this project.</Text>
            )}
          </div>
        }
        confirmDisabled={sharedInfoLoading}
      />
      <SEConfirmModal
        opened={sharedUserProfileModalOpen}
        onClose={() => { setSharedUserProfileModalOpen(false); setSharedUserProfileData(null); }}
        onlyOk
        confirmLabel="OK"
        header="User Info"
        message={
          sharedUserProfileLoading ? (
            <se-loading loading={sharedUserProfileLoading} />
          ) : sharedUserProfileError ? (
            <Text size="sm" color="red">{sharedUserProfileError}</Text>
          ) : sharedUserProfileData ? (
            (() => {
              const statusText = sharedUserProfileData.status === 'A' ? 'Active' : 'Inactive';
              let updatedDisplay = sharedUserProfileData.updated_at || '—';
              const match = /^(\d{4})\/(\d{2})\/(\d{2}) (\d{2}:\d{2}:\d{2})$/.exec(updatedDisplay);
              if (match) {
                const [ , y, m, d, t ] = match;
                const dateObj = new Date(`${y}-${m}-${d}T${t}Z`);
                if (!isNaN(dateObj.getTime())) {
                  const opts = { month:'short', day:'2-digit', year:'numeric' };
                  const datePart = dateObj.toLocaleDateString(undefined, opts);
                  updatedDisplay = `${datePart} ${t}`;
                }
              }
              return (
                <Grid gutter="md" align="flex-start" sx={{ minWidth:'360px' }}>
                  <Col span={8}>
                    <Stack spacing="sm">
                      <div>
                        <Text size="xs" transform="uppercase" weight={600} color="dimmed">Name</Text>
                        <Text weight={600}>{sharedUserProfileData?.firstName + ' ' + sharedUserProfileData?.lastName || '—'}</Text>
                      </div>
                      <div>
                        <Text size="xs" transform="uppercase" weight={600} color="dimmed">Email</Text>
                        <Text size="sm" sx={{ wordBreak:'break-all' }}>{sharedUserProfileData.email || '—'}</Text>
                      </div>
                    </Stack>
                  </Col>
                  <Col span={4}>
                    <Group position="center" sx={{ height:'100%' }}>
                      {sharedUserProfileData?.imageLink ? (
                        <Avatar src={convertPictureToBlobUrl(sharedUserProfileData?.imageLink)} size={90} radius="md" alt={sharedUserProfileData.name || 'Profile'} />
                      ) : (
                        <Avatar size={90} radius="md" color="gray">{sharedUserProfileData.name?.[0] || '?'}</Avatar>
                      )}
                    </Group>
                  </Col>
                </Grid>
              );
            })()
          ) : (
            <Text size="sm" color="dimmed">No data</Text>
          )
        }
      />
    </>
  );
};
export default DataTable;